import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { EmployeeDetailDialog } from "../components/employe/EmployeeDetailDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { IdentiteLegaleInstallateur } from "../components/employe/IdentiteLegaleInstallateur";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { AssistantNouvelEmploye } from "../components/employe/AssistantNouvelEmploye";  // 👥 Employés E4b
import { DialogueModifsNonEnregistrees } from "../components/employe/DialogueModifsNonEnregistrees";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { GradeCCQEditor } from "../components/GradeCCQEditor";
// 🆕 [Phase 72 — 2026-06-21] Photos permis de conduire (recto/verso)
import { CoherenceEmployesComptes } from "../components/employes/CoherenceEmployesComptes";  // 👥 Employés E1
import { SectionIdentite } from "../components/employe/sections/SectionIdentite";  // 👥 Employés E4
import { SectionEmploi } from "../components/employe/sections/SectionEmploi";
import { SectionRemuneration } from "../components/employe/sections/SectionRemuneration";
import { SectionFormations } from "../components/employe/sections/SectionFormations";  // 👥 Employés E4b
import { SectionVetements } from "../components/employe/sections/SectionVetements";
import { SectionDocuments } from "../components/employe/sections/SectionDocuments";
import { SectionConges } from "../components/employe/sections/SectionConges";
import { SectionAvertissements } from "../components/employe/sections/SectionAvertissements";
import { SectionEvaluations } from "../components/employe/sections/SectionEvaluations";
import { EditionFicheProvider } from "../hooks/useEditionSection";
import { BandeauFicheArchivee } from "../components/employes/Regroupements"; // 👥 E5
import { TabsFicheGardes } from "../components/employe/TabsFicheGardes";  // 👥 E4b : garde au changement d'onglet
import { BlocStatutEmploye } from "../components/employes/BlocStatutEmploye";  // 👥 Employés E2
import { useAuth } from "../lib/AuthContext";
import { usePermissions } from "../lib/PermissionsContext";
import { 
  Users, Plus, Search, Edit, Trash2, Phone, Mail, MapPin, 
  Calendar, DollarSign, GraduationCap, AlertTriangle, FileSignature,
  Clock, User, Building, Briefcase, ChevronRight, Eye, ShirtIcon, Shield,
  ArrowUpCircle, RefreshCw, CheckCircle2, XCircle, Key, Pencil, ClipboardList
} from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';


export function GestionEmployes() {
  const { user, isProprietaire } = useAuth();
  const { canSeeEmployeTab } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [congesSoldes, setCongesSoldes] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  // 🆕 2026-05-23 — Toggle pour afficher aussi les employés inactifs.
  // Par défaut: false → seulement les actifs (cohérence avec partout dans l'app).
  const [showInactifs, setShowInactifs] = useState(false);
  const [accesResume, setAccesResume] = useState({}); // 👥 E2 : fiche → statut d'accès (badges discrets, propriétaire)
  const ficheSaleRef = useRef(false); // 👥 E4 : garde « modifications non enregistrées » à la fermeture de la fiche
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalKey, setCreateModalKey] = useState(0);
  const [fermetureEnAttente, setFermetureEnAttente] = useState(false); // 👥 E4b : dialogue de l'app à la fermeture de la fiche
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Modal promotion chef d'équipe
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [promotionMode, setPromotionMode] = useState("remplacer"); // "remplacer" | "nouvelle"
  const [promotionChefCible, setPromotionChefCible] = useState("");
  const [promotionLoading, setPromotionLoading] = useState(false);

  // Modal transfert BTs (réparation)
  const [showTransfertBTsModal, setShowTransfertBTsModal] = useState(false);
  const [transfertSourceId, setTransfertSourceId] = useState("");
  const [transfertLoading, setTransfertLoading] = useState(false);

  // Onglet Permis
  const [permisTypes, setPermisTypes] = useState([]);
  const [permisLoading, setPermisLoading] = useState(false);
  const [newPermisNom, setNewPermisNom] = useState("");
  const [addingPermisType, setAddingPermisType] = useState(false);
  const [togglingPermis, setTogglingPermis] = useState(null); // permis_id en cours

  // 🐛 2026-05-31 — Bug fix : l'onglet Permis ne chargeait pas au 1er clic.
  // L'ancien code mettait `onClick={loadPermisTypes}` sur le TabsTrigger Radix,
  // mais l'événement onClick d'un Trigger Radix peut perdre la course avec le
  // changement de tab (le TabsContent montait avec permisTypes=[] avant que
  // le fetch démarre). Solution : déclencher le chargement via useEffect qui
  // surveille `activeTab` — robuste et toujours appelé.
  useEffect(() => {
    if (activeTab !== "permis") return;
    if (permisTypes.length > 0 || permisLoading) return;
    setPermisLoading(true);
    axios.get(`${API_URL}/api/permis-types`)
      .then(res => setPermisTypes(res.data.types || []))
      .catch(() => toast.error("Erreur chargement des permis"))
      .finally(() => setPermisLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Véhicules (pour sélecteur chef d'équipe)
  const [vehiculesList, setVehiculesList] = useState([]);

  // 🆕 2026-05-06 — anciens chefs (rétrogradés mais encore référencés sur des BT/équipes)
  const [anciensChefs, setAnciensChefs] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/employees/anciens-chefs`)
      .then(r => setAnciensChefs(r.data?.anciens_chefs || []))
      .catch(() => setAnciensChefs([]));
  }, [employees]);

  // Fetch soldes quand on ouvre le détail d'un employé
  useEffect(() => {
    if (!selectedEmployee?.id) { setCongesSoldes(null); return; }
    axios.get(`${API_URL}/api/conges/solde/${selectedEmployee.id}`)
      .then(r => r.status === 200 ? r.data : null)
      .then(d => setCongesSoldes(d))
      .catch(() => {});
  }, [selectedEmployee?.id]);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, filterStatut, filterType, filterDept, showInactifs]);

  useEffect(() => {
    loadEmployees();
  }, [showInactifs]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.getEmployees({ include_inactive: showInactifs });
      setEmployees(response.employees || []);
    } catch (error) {
      console.error("Erreur chargement employés:", error);
      toast.error("Erreur lors du chargement des employés");
    } finally {
      setLoading(false);
    }
  };

  const handlePromotionChef = async () => {
    if (!selectedEmployee) return;
    if (promotionMode === "remplacer" && !promotionChefCible) {
      toast.error("Sélectionnez un chef à remplacer");
      return;
    }
    setPromotionLoading(true);
    try {
      const body = promotionMode === "remplacer"
        ? { remplacer_chef_id: promotionChefCible }
        : { remplacer_chef_id: null };
      const res = await axios.post(`${API_URL}/api/employees/${selectedEmployee.id}/promouvoir-chef`, body);
      toast.success(res.data.message);
      setShowPromotionModal(false);
      setPromotionChefCible("");
      await loadEmployees();
      // Rafraîchir l'employé sélectionné
      setSelectedEmployee(prev => res.data.employee || prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la promotion");
    } finally {
      setPromotionLoading(false);
    }
  };

  // ── Permis opérationnels ──
  const handleTransfertBTs = async () => {
    if (!selectedEmployee || !transfertSourceId) return;
    setTransfertLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/employees/transferer-bts`, {
        ancien_chef_id: transfertSourceId,
        nouveau_chef_id: selectedEmployee.id,
      });
      toast.success(res.data.message);
      setShowTransfertBTsModal(false);
      setTransfertSourceId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du transfert des BTs");
    } finally {
      setTransfertLoading(false);
    }
  };

  // ── Permis opérationnels ──
  const loadPermisTypes = async () => {
    setPermisLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/permis-types`);
      setPermisTypes(res.data.types || []);
    } catch (e) {
      toast.error("Erreur chargement des permis");
    } finally {
      setPermisLoading(false);
    }
  };

  const handleTogglePermis = async (permisId, permisNom, hasPermis) => {
    setTogglingPermis(permisId);
    try {
      if (hasPermis) {
        await axios.delete(`${API_URL}/api/employees/${selectedEmployee.id}/permis/${permisId}`);
        toast.success(`Permis "${permisNom}" retiré`);
      } else {
        await axios.post(`${API_URL}/api/employees/${selectedEmployee.id}/permis/${permisId}/accorder`);
        toast.success(`Permis "${permisNom}" accordé`);
      }
      // Rafraîchir l'employé
      const empRes = await axios.get(`${API_URL}/api/employees/${selectedEmployee.id}`);
      setSelectedEmployee(empRes.data);
      setEmployees(prev => prev.map(e => e.id === selectedEmployee.id ? empRes.data : e));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur modification permis");
    } finally {
      setTogglingPermis(null);
    }
  };

  const handleAddPermisType = async () => {
    if (!newPermisNom.trim()) return;
    setAddingPermisType(true);
    try {
      await axios.post(`${API_URL}/api/permis-types`, { nom: newPermisNom.trim() });
      setNewPermisNom("");
      await loadPermisTypes();
      toast.success(`Permis "${newPermisNom}" créé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création du permis");
    } finally {
      setAddingPermisType(false);
    }
  };

  const handleDeletePermisType = async (permisId, permisNom) => {
    if (!window.confirm(`Supprimer le permis "${permisNom}" de tous les employés ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/permis-types/${permisId}`);
      await loadPermisTypes();
      toast.success(`Permis "${permisNom}" supprimé`);
    } catch (e) {
      toast.error("Erreur suppression du permis");
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.prenom?.toLowerCase().includes(term) ||
        e.nom?.toLowerCase().includes(term) ||
        e.email?.toLowerCase().includes(term)
      );
    }
    
    if (filterStatut !== "all") {
      filtered = filtered.filter(e => e.statut === filterStatut);
    }
    
    if (filterType !== "all") {
      filtered = filtered.filter(e => e.type_employe === filterType);
    }
    
    if (filterDept !== "all") {
      filtered = filtered.filter(e => e.departement === filterDept);
    }

    // 🆕 2026-05-23 — Si showInactifs=false, on cache aussi en local (au cas où
    // l'API renverrait des inactifs malgré tout, ceinture et bretelles).
    if (!showInactifs) {
      filtered = filtered.filter(e => e.est_actif !== false);
    }

    setFilteredEmployees(filtered);
  };


  const openEmployeeDetail = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailModal(true);
    // 🆕 [Phase 73 — 2026-06-21] Deep-link via URL hash : si la fiche est ouverte
    // avec un hash valide (#permis, #avertissements…), on active ce tab. Sinon "info".
    const hash = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
    const validHashes = ["info", "salaire", "vetements", "formations", "avertissements", "contrats", "conges", "permis"];
    setActiveTab(validHashes.includes(hash) ? hash : "info");
  };

  // 🆕 [Phase 73 — 2026-06-21] Sync `activeTab` ↔ URL hash en mode fiche ouverte.
  // Permet à l'utilisatrice de partager un lien direct vers un onglet précis
  // (ex: fiche?id=X#permis) ou de rafraîchir la page sans perdre son contexte.
  useEffect(() => {
    if (!showDetailModal || !activeTab) return;
    if (typeof window === 'undefined') return;
    const newHash = `#${activeTab}`;
    if (window.location.hash !== newHash) {
      try {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${newHash}`);
      } catch {
        // ignore (safari très ancien)
      }
    }
  }, [activeTab, showDetailModal]);

  // 🆕 [Phase 73] Nettoie le hash quand la fiche se ferme (évite hash résiduel
  // qui rouvre l'onglet précédent lors d'une réouverture de fiche différente).
  useEffect(() => {
    if (showDetailModal) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash) {
      try {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      } catch {
        // ignore
      }
    }
  }, [showDetailModal]);

  // 👥 E2 : les transitions de statut passent par le bloc « Statut » (BlocStatutEmploye) ; cette fonction met à jour l'état local
  const handleStatutChange = (fiche) => {
    setSelectedEmployee(prev => prev ? { ...prev, ...fiche } : prev);
    setEmployees(prev => prev.map(e => e.id === fiche.id ? { ...e, ...fiche } : e));
    loadAccesResume();
  };

  const loadAccesResume = async () => {
    if (!isProprietaire) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/employes/acces-resume`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
      setAccesResume(data.acces || {});
    } catch { /* badge informatif seulement */ }
  };

  useEffect(() => { loadAccesResume(); }, [isProprietaire, employees.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 👥 E2 : ouverture directe d'une fiche via ?employe=<id> (lien « Ouvrir la fiche » depuis Comptes et accès)
  useEffect(() => {
    if (typeof window === 'undefined' || employees.length === 0) return;
    const id = new URLSearchParams(window.location.search).get('employe');
    if (id && !showDetailModal) {
      const e = employees.find((x) => x.id === id);
      if (e) openEmployeeDetail(e);
      else axios.get(`${API_URL}/api/employees/${id}`).then((r) => openEmployeeDetail(r.data)).catch(() => {}); // 👥 E5 : fiche archivée (hors liste) lisible
    }
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleActif = async (employee) => {
    try {
      const newStatus = employee.est_actif === false ? true : false;
      const res = await api.updateEmployee(employee.id, { est_actif: newStatus });
      toast.success(newStatus ? `${employee.prenom} réactivé!` : `${employee.prenom} mis en inactif`);
      (res?.avertissements || []).forEach((a) => toast.warning(a, { duration: 10000 })); // 👥 E1
      setSelectedEmployee(prev => prev ? { ...prev, est_actif: newStatus } : prev);
      setEmployees(prev => prev.map(e => e.id === employee.id ? { ...e, est_actif: newStatus } : e));
    } catch (err) {
      console.error("Erreur toggle actif:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const handleDeleteEmployee = async () => {
    if (!deleteConfirm) return;
    try {
      const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
      const res = await axios.delete(`${API_URL}/api/employees/${deleteConfirm.id}`);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(`${deleteConfirm.prenom} ${deleteConfirm.nom} supprimé`);
      setDeleteConfirm(null);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      loadEmployees();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatutBadge = (statut) => {
    const colors = {
      employe: "bg-blue-100 text-blue-700",
      sous_traitant: "bg-purple-100 text-purple-700"
    };
    const labels = {
      employe: "Employé",
      sous_traitant: "Sous-traitant"
    };
    return <Badge className={colors[statut] || "bg-gray-100"}>{labels[statut] || statut}</Badge>;
  };

  const getTypeBadge = (type, roleAdmin, roleTerrain, statut) => {
    // Pour les sous-traitants, le badge statut l'affiche déjà — on ne duplique pas
    if (statut === 'sous_traitant' || type === 'sous_traitant') {
      return null;
    }
    if (type === 'admin') {
      const roleLabels = {
        charge_projet: "Chargé(e) de projet",
        adjoint_admin: "Adjoint admin.",
        admin: "Admin"
      };
      return <Badge className="bg-orange-100 text-orange-700">{roleLabels[roleAdmin] || "Admin"}</Badge>;
    }
    if (type === 'terrain') {
      const roleLabels = { chef_equipe: "Chef d'équipe", peintre: "Peintre" };
      return <Badge className="bg-green-100 text-green-700">{roleLabels[roleTerrain] || "Terrain"}</Badge>;
    }
    if (type === 'representant') return <Badge className="bg-cyan-100 text-cyan-700">Représentant</Badge>;
    return <Badge className="bg-gray-100 text-gray-700">{type || "—"}</Badge>;
  };

  const getDeptBadge = (dept) => {
    const colors = {
      PRQC: "bg-emerald-100 text-emerald-700",
      LMDS: "bg-violet-100 text-violet-700",
      LOCATION: "bg-amber-100 text-amber-700"
    };
    return <Badge className={colors[dept] || "bg-gray-100"}>{dept}</Badge>;
  };

  // Stats
  const stats = {
    total: employees.length,
    actifs: employees.filter(e => e.est_actif !== false).length,
    terrain: employees.filter(e => e.type_employe === "terrain").length,
    admin: employees.filter(e => e.type_employe === "admin").length,
    saisonniers: employees.filter(e => e.est_saisonnier).length
  };

  return (
    <div className="space-y-6" data-testid="gestion-employes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="h-7 w-7 text-emerald-600" />
            Gestion des Employés
          </h1>
          <p className="text-slate-500 mt-1">Fiches complètes, formations, avertissements et historique</p>
        </div>
        <Button 
        onClick={() => { setShowCreateModal(true); setCreateModalKey(k => k + 1); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-employee-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvel Employé
        </Button>
      </div>

      {/* 👥 Employés E1 — rapport de cohérence (propriétaire) */}
      {isProprietaire && (
        <CoherenceEmployesComptes
          onOuvrirFiche={(id) => { const e = employees.find((x) => x.id === id); if (e) openEmployeeDetail(e); else toast.error("Fiche introuvable dans la liste chargée."); }}
          onOuvrirCompte={(id) => window.location.assign(`/gestion-utilisateurs?user=${id}`)}
        />
      )}

      {/* Boutons synchronisation admin — Propriétaire uniquement */}
      {isProprietaire && (
        <div className="flex flex-wrap gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-xs font-semibold text-amber-700 flex items-center mr-2">Outils Admin :</span>
          <Button
            data-testid="btn-sync-salaires"
            size="sm" variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-100 text-xs"
            onClick={async () => {
              try {
                const r = await axios.post(`${API_URL}/api/employees/sync-salaires-all`);
                toast.success(`Salaires synchronisés — ${r.data.updated} employé(s) mis à jour`);
              } catch { toast.error('Erreur sync salaires'); }
            }}
          >
            Sync Salaires
          </Button>
          <Button
            data-testid="btn-sync-cp-noms"
            size="sm" variant="outline"
            className="border-blue-400 text-blue-800 hover:bg-blue-100 text-xs"
            onClick={async () => {
              try {
                const r = await axios.post(`${API_URL}/api/crm/sync-cp-noms`);
                toast.success(`Noms CP synchronisés — ${r.data.updated} dossier(s) mis à jour`);
              } catch { toast.error('Erreur sync noms CP'); }
            }}
          >
            Sync Noms CP (dossiers)
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Actifs</p>
                <p className="text-2xl font-bold text-green-700">{stats.actifs}</p>
              </div>
              <User className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Terrain</p>
                <p className="text-2xl font-bold text-blue-700">{stats.terrain}</p>
              </div>
              <Briefcase className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Admin</p>
                <p className="text-2xl font-bold text-orange-700">{stats.admin}</p>
              </div>
              <Building className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cyan-600">Saisonniers</p>
                <p className="text-2xl font-bold text-cyan-700">{stats.saisonniers}</p>
              </div>
              <Calendar className="h-8 w-8 text-cyan-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Permanents</p>
                <p className="text-2xl font-bold text-emerald-700">{employees.filter(e => e.est_actif !== false && !e.est_saisonnier && e.permanence === 'permanent').length}</p>
              </div>
              <Shield className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 🚀 [Phase 107.C — 2026-07-14] Section "Équipes actives" REMPLACÉE
           par une simple liste des employés terrain (chefs d'équipe + peintres).
           Le regroupement par équipe (peintres attachés à un chef) est obsolète
           depuis Phase 107.A/B (fin des équipes prédéterminées). L'ancienne vue
           affichait les équipes ; la nouvelle vue affiche simplement qui est
           chef et qui est peintre. */}
      {(() => {
        const terrainEmployees = employees.filter(e => e.est_actif !== false && e.type_employe === 'terrain');
        const chefs = terrainEmployees.filter(e => e.role_terrain === 'chef_equipe');
        const peintres = terrainEmployees.filter(e =>
          e.role_terrain !== 'chef_equipe' &&
          e.role_terrain !== 'sous_traitant' &&
          e.departement !== 'LMDS'
        );
        return (
          <Card data-testid="section-employes-terrain">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  Employés terrain ({terrainEmployees.length})
                  <span className="text-[10px] text-slate-400 font-normal italic ml-2">
                    Chefs : {chefs.length} · Peintres : {peintres.length}
                  </span>
                </h3>
              </div>

              {/* Bloc Chefs d'équipe (avec pastille couleur) */}
              {chefs.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-blue-700 mb-2">Chefs d'équipe</p>
                  <div className="flex flex-wrap gap-2" data-testid="liste-chefs-equipe">
                    {chefs.map(c => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-2 text-xs bg-blue-50 text-blue-800 border border-blue-200 px-2 py-1 rounded-lg"
                      >
                        {c.couleur_equipe && (
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full border"
                            style={{ backgroundColor: c.couleur_equipe }}
                          />
                        )}
                        {c.prenom} {c.nom}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.permanence === 'saisonnier' ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {c.permanence === 'saisonnier' ? 'Saison.' : 'Perm.'}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Bloc Peintres */}
              {peintres.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-2">Peintres</p>
                  <div className="flex flex-wrap gap-2" data-testid="liste-peintres">
                    {peintres.map(p => (
                      <span
                        key={p.id}
                        className="inline-flex items-center gap-2 text-xs bg-slate-50 text-slate-700 border border-slate-200 px-2 py-1 rounded-lg"
                      >
                        {p.prenom} {p.nom}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.permanence === 'saisonnier' || p.est_saisonnier ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {p.permanence === 'saisonnier' || p.est_saisonnier ? 'Saison.' : 'Perm.'}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {chefs.length === 0 && peintres.length === 0 && (
                <p className="text-xs text-slate-400 italic">Aucun employé terrain actif.</p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-employee-input"
              />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-full md:w-[150px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="employe">Employés</SelectItem>
                <SelectItem value="sous_traitant">Sous-traitants</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="terrain">Terrain</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="representant">Représentant</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="w-full md:w-[150px]">
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous départements</SelectItem>
                <SelectItem value="PRQC">PRQC</SelectItem>
                <SelectItem value="LMDS">LMDS</SelectItem>
                <SelectItem value="LOCATION">Location</SelectItem>
              </SelectContent>
            </Select>
            {/* 🆕 2026-05-23 — Toggle "Afficher les inactifs" */}
            <label
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors text-sm whitespace-nowrap"
              data-testid="toggle-inactifs-label"
            >
              <input
                type="checkbox"
                checked={showInactifs}
                onChange={(e) => setShowInactifs(e.target.checked)}
                className="w-4 h-4 accent-emerald-600"
                data-testid="toggle-inactifs-checkbox"
              />
              <span className="text-slate-700">Afficher inactifs</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Employés</CardTitle>
          <CardDescription>{filteredEmployees.length} employé(s) trouvé(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun employé trouvé</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un employé
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEmployees.map((employee) => (
                <div 
                  key={employee.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-slate-50 cursor-pointer transition-all"
                  onClick={() => openEmployeeDetail(employee)}
                  data-testid={`employee-row-${employee.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <EmployeeAvatar photoUrl={employee.photo_url} prenom={employee.prenom} nom={employee.nom} size="md" />
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-slate-800 truncate">
                        {employee.prenom} {employee.nom}
                        {employee.est_actif === false && (
                          <Badge className="ml-2 bg-red-100 text-red-700">Inactif</Badge>
                        )}
                        {employee.est_saisonnier && employee.est_actif !== false && (
                          <Badge className="ml-2 bg-cyan-100 text-cyan-700">Saisonnier</Badge>
                        )}
                        {!employee.est_saisonnier && employee.permanence === 'permanent' && employee.est_actif !== false && !employee.est_proprietaire && (
                          <Badge className="ml-2 bg-emerald-100 text-emerald-700">Permanent</Badge>
                        )}
                        {employee.est_proprietaire && (
                          <Badge className="ml-2 bg-amber-100 text-amber-700">Proprio</Badge>
                        )}
                        {/* 👥 E2 — badge discret : fiche active dont l'accès est suspendu manuellement ou sans compte (information seulement) */}
                        {employee.est_actif !== false && accesResume[employee.id] === 'suspendu_manuel' && (
                          <Badge variant="outline" className="ml-2 border-red-300 text-red-600 text-[10px]" data-testid={`badge-acces-${employee.id}`}>Accès suspendu</Badge>
                        )}
                        {employee.est_actif !== false && accesResume[employee.id] === 'aucun_compte' && !employee.est_proprietaire && (
                          <Badge variant="outline" className="ml-2 border-slate-300 text-slate-500 text-[10px]" data-testid={`badge-acces-${employee.id}`}>Sans compte</Badge>
                        )}
                      </h3>
                      <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                        {employee.email && (
                          <span className="flex items-center gap-1 truncate max-w-[180px]">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{employee.email}</span>
                          </span>
                        )}
                        {employee.telephone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            {employee.telephone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center flex-wrap justify-end gap-1.5 flex-shrink-0 max-w-[120px] sm:max-w-none">
                    <span className="hidden sm:contents">
                      {getDeptBadge(employee.departement)}
                      {getStatutBadge(employee.statut)}
                    </span>
                    {getTypeBadge(employee.type_employe, employee.role_admin, employee.role_terrain, employee.statut)}
                    {employee.avertissements?.length > 0 && (
                      <Badge className="bg-red-100 text-red-700">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {employee.avertissements.length}
                      </Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 👥 E4b — Assistant « Nouvel employé » (3 étapes) ; la fiche unifiée remplace l'ancien formulaire complet */}
      <AssistantNouvelEmploye
        key={createModalKey}
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        isProprietaire={isProprietaire}
        onCree={async (emp) => { await loadEmployees(); setSelectedEmployee(emp); setActiveTab('info'); setShowDetailModal(true); }}
      />

      {/* Employee Detail Modal */}
      {/* 🆕 [Phase 103 — 2026-07-09] EmployeeDetailDialog local (overlay clair
          + X redesigné) au lieu du Dialog shadcn global (bg-black/80). */}
      <EmployeeDetailDialog open={showDetailModal} onOpenChange={(o) => { if (!o && ficheSaleRef.current) { setFermetureEnAttente(true); return; } setShowDetailModal(o); }} className="bg-slate-50/60">
          {selectedEmployee && (
            <>
              <DialogHeader className="pb-2">
                <div className="flex items-start justify-between gap-4 pr-14 bg-white rounded-xl border border-slate-200 px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <EmployeeAvatar photoUrl={selectedEmployee.photo_url} prenom={selectedEmployee.prenom} nom={selectedEmployee.nom} size="lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <DialogTitle className="text-2xl font-bold text-slate-900 truncate" data-testid="employee-detail-title">
                        {selectedEmployee.prenom} {selectedEmployee.nom}
                      </DialogTitle>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {selectedEmployee.numero_employe && (
                          <span className="bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-xs font-mono text-slate-700 font-semibold" data-testid="badge-numero-employe">
                            ID: {selectedEmployee.numero_employe}
                          </span>
                        )}
                        {getDeptBadge(selectedEmployee.departement)}
                        {getStatutBadge(selectedEmployee.statut)}
                        {getTypeBadge(selectedEmployee.type_employe, selectedEmployee.role_admin, selectedEmployee.role_terrain, selectedEmployee.statut)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
                        {selectedEmployee.email && (
                          <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedEmployee.email}</span>
                        )}
                        {selectedEmployee.telephone && (
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{selectedEmployee.telephone}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* 👥 E4b — le bouton « Modifier » global a disparu : chaque section se modifie en place */}
                </div>
              </DialogHeader>

              <BandeauFicheArchivee employee={selectedEmployee} conservee={employees.find((x) => x.id === selectedEmployee.fusionnee_dans)} onOuvrirFiche={(id) => { const e = employees.find((x) => x.id === id); if (e) openEmployeeDetail(e); }} />
              <EditionFicheProvider employee={selectedEmployee} onSaved={handleStatutChange} onSaleChange={(v) => { ficheSaleRef.current = v; }}>
              <TabsFicheGardes value={activeTab} onValueChange={setActiveTab} className="mt-4">
                {/* 🐛 [Hotfix v13.36 — 2026-07-09] Bug #3 : plus jamais de scroll
                    horizontal — flex-wrap fait passer les onglets sur 2 lignes
                    si l'écran est trop étroit. h-auto pour permettre le wrap. */}
                <TabsList className="flex flex-wrap w-full h-auto bg-white border border-slate-200 shadow-sm rounded-lg p-1 gap-0.5" data-testid="employee-tabs-list"><TabsTrigger value="info" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50">
                    <User className="h-4 w-4" />
                    Identité
                  </TabsTrigger>
                  {canSeeEmployeTab('emploi') && (
                    <TabsTrigger value="emploi" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 rounded-md text-sm font-medium transition-colors" data-testid="tab-emploi">
                      <Briefcase className="h-4 w-4" />
                      Emploi & rôles
                    </TabsTrigger>
                  )}
                  {canSeeEmployeTab('salaire') && (
                    <TabsTrigger value="salaire" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50">
                      <DollarSign className="h-4 w-4" />
                      Salaire
                    </TabsTrigger>
                  )}
                  {canSeeEmployeTab('vetements') && (
                    <TabsTrigger value="vetements" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50">
                      <ShirtIcon className="h-4 w-4" />
                      Vêtements
                    </TabsTrigger>
                  )}
                  {canSeeEmployeTab('formations') && (
                    <TabsTrigger value="formations" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50">
                      <GraduationCap className="h-4 w-4" />
                      Formations
                    </TabsTrigger>
                  )}
                  {canSeeEmployeTab('avertissements') && (
                    <TabsTrigger value="avertissements" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50">
                      <AlertTriangle className="h-4 w-4" />
                      Avertissements
                      {selectedEmployee.avertissements?.length > 0 && (
                        <Badge className="ml-1 bg-red-500 text-white text-xs">
                          {selectedEmployee.avertissements.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  )}
                  {(canSeeEmployeTab('contrats') || canSeeEmployeTab('permis')) && (
                    <TabsTrigger value="documents" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50" data-testid="tab-documents">
                      <FileSignature className="h-4 w-4" />
                      Documents & permis
                      {selectedEmployee.permis_accordes?.length > 0 && (
                        <Badge className="ml-1 bg-emerald-500 text-white text-xs">{selectedEmployee.permis_accordes.length}</Badge>
                      )}
                    </TabsTrigger>
                  )}
                  {canSeeEmployeTab('conges') && (
                    <TabsTrigger value="conges" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50">
                      <Calendar className="h-4 w-4" />
                      Congés
                    </TabsTrigger>
                  )}
                  {/* 📝 [Phase 103 — 2026-07-09] Nouvel onglet Évaluations */}
                  {canSeeEmployeTab('evaluations') && <TabsTrigger value="evaluations" className="flex items-center gap-1.5 h-9 px-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:rounded-b-none text-slate-600 hover:text-slate-900 hover:bg-slate-50" data-testid="tab-evaluations">
                    <ClipboardList className="h-4 w-4" />
                    Évaluations
                  </TabsTrigger>}
                  </TabsList>
                
                <TabsContent value="info" className="mt-4">
                  {/* 👥 E4 — Section 1 : Identité & coordonnées (lecture = édition) */}
                  {canSeeEmployeTab('infos') ? (
                    <SectionIdentite
                      key={`ident-${selectedEmployee.id}`}
                      employee={selectedEmployee}
                      isProprietaire={isProprietaire}
                      onStatutChange={handleStatutChange}
                      onInviter={(emp) => window.location.assign(`/gestion-utilisateurs?inviter=${emp.id}`)}
                    />
                  ) : <p className="text-sm text-slate-500" data-testid="section-infos-refusee">Vous n'avez pas accès à cette section.</p>}
                </TabsContent>

                <TabsContent value="emploi" className="mt-4">
                  {/* 👥 E4 — Section 2 : Emploi, rôles & accès */}
                  {canSeeEmployeTab('emploi') && (
                    <SectionEmploi key={`emploi-${selectedEmployee.id}`} employee={selectedEmployee} isProprietaire={isProprietaire} onRolesSaved={handleStatutChange} />
                  )}
                </TabsContent>

                <TabsContent value="salaire" className="mt-4">
                  {/* 👥 E4 — Section 3 : Rémunération & bancaire */}
                  {canSeeEmployeTab('salaire') && (
                    <SectionRemuneration
                      key={`sal-${selectedEmployee.id}`}
                      employee={selectedEmployee}
                      isProprietaire={isProprietaire}
                      onGradeSaved={async (newGrade) => {
                        try {
                          const r = await axios.get(`${API_URL}/api/employees/${selectedEmployee.id}`);
                          setSelectedEmployee(r.data);
                        } catch {
                          setSelectedEmployee(prev => prev ? { ...prev, grade_ccq: newGrade } : prev);
                        }
                        loadEmployees();
                      }}
                    />
                  )}
                </TabsContent>

                {/* 👥 E4b — Sections 4 à 9 : lecture = édition, gating lecture ET édition par clé d'onglet */}
                <TabsContent value="formations" className="mt-4">
                  {canSeeEmployeTab('formations') && <SectionFormations key={`form-${selectedEmployee.id}`} employee={selectedEmployee} isProprietaire={isProprietaire} />}
                </TabsContent>
                <TabsContent value="vetements" className="mt-4">
                  {canSeeEmployeTab('vetements') && <SectionVetements key={`vet-${selectedEmployee.id}`} employee={selectedEmployee} />}
                </TabsContent>
                <TabsContent value="documents" className="mt-4" data-testid="tab-content-documents">
                  {(canSeeEmployeTab('contrats') || canSeeEmployeTab('permis')) && (
                    <SectionDocuments
                      key={`doc-${selectedEmployee.id}`}
                      employee={selectedEmployee}
                      isProprietaire={isProprietaire}
                      voirContrats={canSeeEmployeTab('contrats')}
                      voirPermis={canSeeEmployeTab('permis')}
                      onEmployeeRefresh={async () => {
                        try {
                          const r = await axios.get(`${API_URL}/api/employees/${selectedEmployee.id}`);
                          setSelectedEmployee(r.data);
                          setEmployees(prev => prev.map(e => e.id === selectedEmployee.id ? r.data : e));
                        } catch (e) {
                          // Silencieux — l'upload a réussi côté backend
                        }
                      }}
                      permis={{ types: permisTypes, loading: permisLoading, toggling: togglingPermis, onToggle: handleTogglePermis, onDelete: handleDeletePermisType, nouveauNom: newPermisNom, setNouveauNom: setNewPermisNom, ajoutEnCours: addingPermisType, onAjouter: handleAddPermisType }}
                    />
                  )}
                </TabsContent>
                <TabsContent value="conges" className="mt-4">
                  {canSeeEmployeTab('conges') && <SectionConges key={`conges-${selectedEmployee.id}`} employee={selectedEmployee} isProprietaire={isProprietaire} soldes={congesSoldes} />}
                </TabsContent>
                <TabsContent value="avertissements" className="mt-4">
                  {canSeeEmployeTab('avertissements') && <SectionAvertissements key={`av-${selectedEmployee.id}`} employee={selectedEmployee} peutGerer={isProprietaire} />}
                </TabsContent>
                <TabsContent value="evaluations" className="mt-4" data-testid="tab-content-evaluations">
                  {canSeeEmployeTab('evaluations') && <SectionEvaluations key={`eval-${selectedEmployee.id}`} employee={selectedEmployee} peutGerer={isProprietaire} />}
                </TabsContent>
              </TabsFicheGardes>
              </EditionFicheProvider>

              {/* Actions : Supprimer (le statut est géré par le bloc « Statut » de l'onglet Identité — 👥 E2) */}
              <div className="flex justify-end items-center mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                  onClick={() => setDeleteConfirm(selectedEmployee)}
                  data-testid="open-delete-employee-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </>
          )}
      </EmployeeDetailDialog>
      <DialogueModifsNonEnregistrees open={fermetureEnAttente} onContinuer={() => setFermetureEnAttente(false)} onAbandonner={() => { setFermetureEnAttente(false); ficheSaleRef.current = false; setShowDetailModal(false); }} />

      {/* Dialog double confirmation suppression */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer un employé</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Êtes-vous sûr de vouloir supprimer <strong>{deleteConfirm?.prenom} {deleteConfirm?.nom}</strong> ?
            </p>
            <p className="text-sm text-orange-600 font-medium">
              Si vous souhaitez conserver ses informations, utilisez plutôt le bouton "Mettre en inactif".
            </p>
            <p className="text-sm text-red-600 font-medium">
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
              <Button variant="destructive" onClick={handleDeleteEmployee} data-testid="confirm-delete-employee">
                <Trash2 className="w-4 h-4 mr-2" /> Oui, supprimer définitivement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal Transfert BTs (réparation) */}
      <Dialog open={showTransfertBTsModal} onOpenChange={setShowTransfertBTsModal}>
        <DialogContent className="max-w-md" data-testid="modal-transfert-bts">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <RefreshCw className="w-5 h-5" />
              Récupérer les BTs d'un autre chef
            </DialogTitle>
            <DialogDescription>
              Transfère tous les bons de travail d'un employé vers <strong>{selectedEmployee?.prenom} {selectedEmployee?.nom}</strong>. Les dates et informations des BTs sont conservées.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Employé source (ancien chef)</Label>
              <Select value={transfertSourceId} onValueChange={setTransfertSourceId}>
                <SelectTrigger data-testid="select-source-bts">
                  <SelectValue placeholder="Sélectionner l'employé dont récupérer les BTs..." />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    .filter(e => e.id !== selectedEmployee?.id)
                    .map(emp => {
                      const btsCount = null; // on ne charge pas le count ici
                      return (
                        <SelectItem key={emp.id} value={emp.id}>
                          <div className="flex items-center gap-2">
                            {emp.couleur_equipe && <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: emp.couleur_equipe }} />}
                            <span>{emp.prenom} {emp.nom}</span>
                            <span className="text-xs text-slate-400">— {emp.role_terrain || emp.type_employe}</span>
                          </div>
                        </SelectItem>
                      );
                    })
                  }
                </SelectContent>
              </Select>
            </div>

            {transfertSourceId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">Ce qui sera fait :</p>
                <ul className="space-y-1 text-xs">
                  <li>• Tous les BTs (non terminés ET historiques) de {employees.find(e => e.id === transfertSourceId)?.prenom} seront réassignés à {selectedEmployee?.prenom}</li>
                  <li>• Les dates de chantier sont conservées</li>
                  <li>• Les clients et informations des BTs sont inchangés</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTransfertBTsModal(false)}>Annuler</Button>
            <Button
              data-testid="btn-confirmer-transfert-bts"
              onClick={handleTransfertBTs}
              disabled={transfertLoading || !transfertSourceId}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              {transfertLoading
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Transfert en cours...</>
                : <><RefreshCw className="w-4 h-4" /> Transférer les BTs</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Promotion Chef d'équipe */}
      <Dialog open={showPromotionModal} onOpenChange={setShowPromotionModal}>
        <DialogContent className="max-w-lg" data-testid="modal-promotion-chef">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ArrowUpCircle className="w-5 h-5" />
              Promouvoir en chef d'équipe
            </DialogTitle>
            <DialogDescription>
              <strong>{selectedEmployee?.prenom} {selectedEmployee?.nom}</strong> deviendra chef d'équipe.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="mode-remplacer"
                onClick={() => setPromotionMode("remplacer")}
                className={`p-3 rounded-lg border-2 text-left transition-all ${promotionMode === "remplacer" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <p className="font-semibold text-sm text-slate-800">Remplacer un chef</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {/* 🚀 [Phase 107.C — 2026-07-14] Le transfert d'équipe est obsolète — seuls rôle + couleur restent transférés */}
                  Récupère la couleur de l'ancien chef (utile pour continuité visuelle)
                </p>
              </button>
              <button
                type="button"
                data-testid="mode-nouvelle"
                onClick={() => { setPromotionMode("nouvelle"); setPromotionChefCible(""); }}
                className={`p-3 rounded-lg border-2 text-left transition-all ${promotionMode === "nouvelle" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <p className="font-semibold text-sm text-slate-800">Nouvelle équipe</p>
                <p className="text-xs text-slate-500 mt-0.5">Devient chef sans remplacer personne</p>
              </button>
            </div>

            {promotionMode === "remplacer" && (
              <div className="space-y-2">
                <Label>Chef à remplacer</Label>
                <Select value={promotionChefCible} onValueChange={setPromotionChefCible}>
                  <SelectTrigger data-testid="select-chef-cible">
                    <SelectValue placeholder="Sélectionner un chef d'équipe..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* 🆕 2026-05-06 — chefs actuels + anciens chefs encore référencés */}
                    {(() => {
                      const chefsActuels = employees.filter(e => e.role_terrain === 'chef_equipe');
                      const idsActuels = new Set(chefsActuels.map(c => c.id));
                      const anciensFiltres = anciensChefs.filter(a => !idsActuels.has(a.id));
                      return (
                        <>
                          {chefsActuels.map(chef => {
                            return (
                              <SelectItem key={chef.id} value={chef.id} data-testid={`chef-actuel-${chef.id}`}>
                                <div className="flex items-center gap-2">
                                  {chef.couleur_equipe && <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: chef.couleur_equipe }} />}
                                  <span>{chef.prenom} {chef.nom}</span>
                                  {/* 🧱 Phase 4 — Lot E : compteur « N membre(s) » (équipe fixe) retiré — toujours 0 depuis la Phase 107 */}
                                </div>
                              </SelectItem>
                            );
                          })}
                          {anciensFiltres.length > 0 && (
                            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-orange-600 font-semibold border-t border-orange-200 mt-1 pt-2">
                              Anciens chefs (rétrogradés)
                            </div>
                          )}
                          {anciensFiltres.map(chef => (
                            <SelectItem key={chef.id} value={chef.id} data-testid={`chef-ancien-${chef.id}`}>
                              <div className="flex items-center gap-2">
                                {chef.couleur_equipe && <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: chef.couleur_equipe }} />}
                                <span>{chef.prenom} {chef.nom}</span>
                                <span className="text-xs text-orange-600 font-medium">— ancien chef</span>
                                <span className="text-xs text-slate-400">· {chef.nb_bts_actifs || 0} BT · {chef.nb_membres_equipe || 0} membre(s)</span>
                              </div>
                            </SelectItem>
                          ))}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>

                {promotionChefCible && (() => {
                  const chefCible = employees.find(e => e.id === promotionChefCible);
                  return (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 text-sm">
                      <p className="font-medium text-slate-700">Aperçu du remplacement :</p>
                      <div className="flex items-center gap-2">
                        {chefCible?.couleur_equipe && <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: chefCible.couleur_equipe }} />}
                        <span className="text-slate-600">Couleur d'équipe transférée</span>
                      </div>
                      {/* 🚀 [Phase 107.C — 2026-07-14] Suppression de la liste "Membres transférés".
                          Le système d'équipes prédéterminées a été supprimé Phase 107.A/B :
                          seul le rôle + la couleur sont transférés. */}
                      <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-xs text-amber-800">
                        <span className="font-semibold">Note Phase 107 :</span> seul le rôle et la couleur d'équipe seront transférés.
                        Les peintres ne sont plus rattachés à un chef d'équipe — chaque employé est sélectionné manuellement au moment de créer un BT.
                      </div>
                      <div className="pt-1 border-t border-slate-200 text-xs text-amber-700 font-medium">
                        {chefCible?.prenom} {chefCible?.nom} sera rétrogradé(e) en peintre.
                      </div>
                    </div>
                  );
                })()}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-start gap-2">
                  <RefreshCw className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                  <span>Les bons de travail futurs seront recalculés automatiquement avec le nouveau coût horaire.</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPromotionModal(false)}>Annuler</Button>
            <Button
              data-testid="btn-confirmer-promotion"
              onClick={handlePromotionChef}
              disabled={promotionLoading || (promotionMode === "remplacer" && !promotionChefCible)}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
            >
              {promotionLoading
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> En cours...</>
                : <><ArrowUpCircle className="w-4 h-4" /> Confirmer la promotion</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GestionEmployes;
